import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject } from 'rxjs';

// Services
import { NavigationParam, RouteParam, ServiceBase } from '../service-base';
import { SaveOptions } from '../../interfaces/save-options';

// Modèles
import { BankAccount } from 'src/app/@shared/models/banking/bank-accounts/bank-account.model';
import { BankAccountRule } from 'src/app/@shared/models/banking/rules/bank-account-rule.model';

const CLASS_NAME = 'BankAccount';
const ENDPOINT = `/bank-accounts/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'duplicates', path: 'duplicates/', childClass: 'BankAccountLine' },
    { key: 'transactions', path: 'transactions/', childClass: 'BankAccountLine' },
]
const ROUTES: RouteParam[] = [
    { key: 'autoMatch', path: '{id}/auto-match/' },
    { key: 'connect', path: 'connect/' },
    { key: 'manage', path: '{id}/manage/' },
    { key: 'merge', path: '{id}/merge/' },
    { key: 'statementUpload', path: '{id}/upload-statement/' },
    { key: 'syncRequest', path: '{id}/sync-request/' },
    { key: 'transactionsUpload', path: '{id}/upload-transactions/' },
]

@Injectable({
    providedIn: 'root'
})
export class BankAccountService extends ServiceBase {

    protected _createdRule$: Subject<BankAccountRule> = new Subject<BankAccountRule>();
    public readonly createdRule$ = this._createdRule$.asObservable();

    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    /**
     * Lance le rapprochement automatique des transactions du compte (`201` sans corps).
     *
     * ⚠️ Appel `httpClient` direct, exceptionnellement : les helpers `_postAction$` /
     * `_postActionValue$` lisent les `messages` d'un corps OData, et le serveur ne renvoie
     * ici aucun corps. On ne rend pas `ODataResponse` tolérant pour une commande réservée
     * aux dossiers de démonstration (aalleard/b4cash-ui#1331).
     */
    public autoMatch$(bankAccount: BankAccount): Observable<boolean> {
        let url = this.routes.autoMatch.replace('{id}', bankAccount.id.toString());
        return this.httpClient.post(url, {}).pipe(
            map(() => true),
            catchError(
                (error: any) => {
                    this.handleError(error);
                    return of(false);
                }
            )
        );
    }

    public connect$(payload: any, options?: SaveOptions): Observable<BankAccount> {
        let url = this.routes.connect;
        return this.create$(payload, { ...options, url: url });
    }

    public getManageUrl$(bankAccount: BankAccount): Observable<any> {
        let url = this.routes.manage.replace('{id}', bankAccount.id.toString());
        return this.httpClient.get(url).pipe(
            map((data: any) => {
                return data.url;
            }),
            catchError(
                (error: any) => {
                    // Manage error
                    this.handleError(error);
                    return of(false);
                }
            )
        );
    }

    public merge$(target: BankAccount, source: BankAccount): Observable<any> {
        return this.update$(source, {
            select: false,
            url: this.routes.merge.replace('{id}', target.id.toString())
        });
    }

    public syncRequest$(bankAccount: BankAccount): Observable<any> {
        let url = this.routes.syncRequest.replace('{id}', bankAccount.id.toString());
        return this.update$(bankAccount, {
            url: url,
        });
    }

    public uploadStatement$(bankAccount: BankAccount, file: File): Observable<any> {
        let url = this.routes.statementUpload.replace('{id}', bankAccount.id.toString());
        return this.uploadFile$(file, url);
    }

    public uploadTransactions$(bankAccount: BankAccount, file: File): Observable<any> {
        let url = this.routes.transactionsUpload.replace('{id}', bankAccount.id.toString());
        return this.uploadFile$(file, url, { successMessage: 'Les transactions vont être traitées en arrière-plan et seront disponible dans quelques instants.' });
    }
}
